import { Line, LineChart, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@volca/ui/components/ui/chart";
import { Stack } from "@volca/ui/components/ui/stack";
import { points } from "@volca/ui/trades";
import { useReportCurrency } from "@/profitability/currency";
import {
  overallMargin,
  pct,
  periodLabel,
  periodOf,
} from "@/profitability/insights/format";
import { useInsights } from "@/profitability/insights/query";
import { isMissing } from "@/profitability/missing";
import { MissingValue, showValue } from "@/profitability/missing-value";
import { roleColumn } from "@/profitability/records/objects";
import type { RecordObject } from "@/profitability/records/objects";
import { SectionHeading } from "@/profitability/records/section-heading";
import { formatCell } from "@/profitability/records/table-values";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type { ReportRow } from "@/profitability/types";
import type { RecordHistoryPeriod } from "./history-query";

const TICK = { fontSize: 12, fill: "var(--subtle)" } as const;

const marginOf = (row: ReportRow | null, total?: string, result?: string) => {
  if (!row || !total || !result) return null;
  // A total or result not in yet makes the margin missing, not zero.
  if (isMissing(row[total]) || isMissing(row[result])) return null;
  const whole = Number(row[total]);
  return whole ? Number(row[result]) / whole : null;
};

/**
 * One record across the template's periods (PRD: row-level history): its
 * margin as a line, against all records of its kind where Insights has them,
 * then a table of each period's figures and the change from the one before.
 * A job that shows up again in a later period with more cost shows it here.
 */
export const RecordHistorySection = ({
  businessId,
  reportId,
  object,
  recordId,
  name,
  periods,
}: {
  businessId: string;
  reportId: string;
  object: RecordObject;
  recordId: string;
  name: string;
  periods: RecordHistoryPeriod[];
}) => {
  const insights = useInsights(businessId, reportId);
  const segment = insights.data?.datasets.find(
    (item) => item.id === object.dataset.id,
  );
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const total = roleColumn(object.dataset, "total");
  // The columns the script lists for a record's history; else what it took
  // in, beside the rate.
  const listed = object.detail?.history
    ?.map((key) => object.dataset.columns.find((column) => column.key === key))
    .filter((column) => !!column);
  const result = roleColumn(object.dataset, "result");
  // The script's history columns as it lists them; when it lists none, what
  // the record took in and the rate worked out from it.
  const columns = listed ?? (total ? [total] : []);
  const workedRate = !listed;
  const present = periods.filter((period) => period.row);
  if (present.length < 2) return null;
  const noun = periodOf(periods);
  const everyone = `All ${object.plural.toLowerCase()}`;
  const data = periods.map((period) => {
    const rows =
      segment &&
      insights.data?.periods.find((item) => item.run_id === period.run_id)
        ?.segments[segment.id];
    return {
      period: periodLabel(period.start_date, noun),
      mine: marginOf(period.row, total?.key, result?.key),
      all: rows ? overallMargin(rows) : null,
    };
  });
  const rows = [...periods].reverse();
  const width = columns.length + (workedRate ? 1 : 0) + 1;
  const header = noun.charAt(0).toUpperCase() + noun.slice(1);
  return (
    <section aria-label="History" className="flex flex-col gap-2">
      <SectionHeading>History</SectionHeading>
      {total && result && (
        <Stack gap={1.5}>
          <div className="-mx-1">
            <ChartContainer
              className="aspect-auto h-28 w-full"
              config={{
                mine: { label: name, color: "var(--ink)" },
                all: { label: everyone, color: "var(--subtlest)" },
              }}
            >
              <LineChart
                data={data}
                margin={{ left: 0, right: 8, top: 6, bottom: 0 }}
                accessibilityLayer
                title={`${name}: ${spec.metric.label} by ${noun}`}
              >
                <XAxis
                  dataKey="period"
                  tick={TICK}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={TICK}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                  domain={["auto", "auto"]}
                  tickFormatter={(value: number) => pct(value, 0)}
                />
                <ChartLegend
                  verticalAlign="top"
                  content={<ChartLegendContent />}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, key) => (
                        <span className="flex w-full justify-between gap-3">
                          <span className="text-subtle">
                            {key === "mine" ? name : everyone}
                          </span>
                          <span className="tabular-nums">
                            {pct(Number(value))}
                          </span>
                        </span>
                      )}
                    />
                  }
                />
                {segment && (
                  <Line
                    dataKey="all"
                    stroke="var(--color-all)"
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}
                <Line
                  dataKey="mine"
                  stroke="var(--color-mine)"
                  strokeWidth={2}
                  connectNulls={false}
                  dot={false}
                  activeDot={{ r: 3 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ChartContainer>
          </div>
        </Stack>
      )}
      {/* Wider than a narrow sheet scrolls in place rather than clipping. */}
      <div
        className="-mx-1 overflow-x-auto px-1"
        role="region"
        tabIndex={0}
        aria-label={`${name} by ${noun}, scrollable`}
      >
        <table
          className="w-full text-sm tabular-nums"
          aria-label={`${name} by ${noun}`}
        >
          <thead>
            <tr className="border-b border-line text-xs text-subtle">
              <th scope="col" className="py-2 pe-3 text-start font-normal">
                {header}
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="py-2 ps-3 text-end font-normal whitespace-nowrap"
                >
                  {column.label ?? column.key}
                </th>
              ))}
              {workedRate && (
                <th
                  scope="col"
                  className="py-2 ps-3 text-end font-normal whitespace-nowrap"
                >
                  {spec.metric.label}
                </th>
              )}
              <th scope="col" className="py-2 ps-3 text-end font-normal">
                Change
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((period, index) => {
              const margin = marginOf(period.row, total?.key, result?.key);
              const before = rows[index + 1];
              const was = before?.comparable
                ? marginOf(before.row, total?.key, result?.key)
                : null;
              const change =
                margin !== null && was !== null ? margin - was : null;
              return (
                <tr key={period.run_id} className="h-9">
                  <th
                    scope="row"
                    className="pe-3 text-start font-normal whitespace-nowrap text-subtle"
                  >
                    {periodLabel(period.start_date, noun)}
                    {recordId && !period.comparable && (
                      <span className="ms-1.5 text-xs text-subtlest">
                        calculation changed
                      </span>
                    )}
                  </th>
                  {period.row ? (
                    <>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="ps-3 text-end whitespace-nowrap"
                        >
                          {showValue(
                            formatCell(
                              period.row?.[column.key] ?? null,
                              column,
                              currency,
                            ),
                          )}
                        </td>
                      ))}
                      {workedRate && (
                        <td className="ps-3 text-end whitespace-nowrap">
                          {margin !== null ? (
                            pct(margin)
                          ) : total &&
                            !isMissing(period.row[total.key]) &&
                            Number(period.row[total.key]) === 0 ? (
                            // A rate of nothing (a callback that took in $0).
                            <span className="text-subtle">—</span>
                          ) : (
                            <MissingValue />
                          )}
                        </td>
                      )}
                      <td className="ps-3 text-end text-xs whitespace-nowrap text-subtle">
                        {change !== null && points(change)}
                      </td>
                    </>
                  ) : (
                    <td
                      colSpan={width}
                      className="ps-3 text-end whitespace-nowrap text-subtle"
                    >
                      Not in this report
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
